import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  Tabs,
  Tab,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Snackbar,
  InputAdornment,
  useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonIcon from '@mui/icons-material/Person';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EmptyStateIllustration from '../assets/empty_state.svg';
import { getUserItineraries, toggleFavorite, getItineraryById } from '../services/itineraryService';
import { getDestinationImage, DEFAULT_DESTINATION_IMAGE } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import useResponsiveSize from '../hooks/useResponsiveSize';

// Add a fallback image URL
const FALLBACK_EMPTY_IMAGE = 'https://cdn-icons-png.flaticon.com/512/6598/6598519.png';

// Sample data for saved trips - Will no longer be used
// const mockSavedTrips = [...]

const TripCard = ({ trip, onToggleFavorite }) => {
  const theme = useTheme();
  const [imageUrl, setImageUrl] = useState(DEFAULT_DESTINATION_IMAGE);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (trip.destination) {
      const destImage = getDestinationImage(trip.destination);
      if (destImage) {
        setImageUrl(destImage);
      }
    }
  }, [trip.destination]);
  
  const handleCardClick = () => {
    navigate(`/itinerary/${trip.id}`);
  };
  
  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    onToggleFavorite(trip.id);
  };

  const formatDateRange = (start, end) => {
    // Use a safer approach to create dates - parse YYYY-MM-DD strings properly
    const createLocalDate = (dateStr) => {
      // If it's already a date, return it
      if (dateStr instanceof Date) return dateStr;
      
      // Handle ISO string or YYYY-MM-DD format
      if (typeof dateStr === 'string') {
        // If in YYYY-MM-DD format, parse it locally
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
          return new Date(year, month - 1, day); // month is 0-indexed in JS Date
        }
        // Otherwise use standard Date constructor
        return new Date(dateStr);
      }
      
      // Fallback
      return new Date();
    };
    
    const startDate = createLocalDate(start);
    const endDate = createLocalDate(end);
    
    const options = { month: 'short', day: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}, ${endDate.getFullYear()}`;
  };
  
  // Determine if trip is upcoming or in the past
  const isUpcoming = new Date(trip.endDate) >= new Date();
  
  // Calculate days remaining or days passed
  const calculateDaysDiff = () => {
    const now = new Date();
    
    // Use same helper function as formatDateRange
    const createLocalDate = (dateStr) => {
      // If it's already a date, return it
      if (dateStr instanceof Date) return dateStr;
      
      // Handle ISO string or YYYY-MM-DD format
      if (typeof dateStr === 'string') {
        // If in YYYY-MM-DD format, parse it locally
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
          return new Date(year, month - 1, day); // month is 0-indexed in JS Date
        }
        // Otherwise use standard Date constructor
        return new Date(dateStr);
      }
      
      // Fallback
      return new Date();
    };
    
    const startDate = createLocalDate(trip.startDate);
    const endDate = createLocalDate(trip.endDate);
    
    // If trip hasn't started yet
    if (startDate > now) {
      const daysUntil = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
      return {
        text: `Starts in ${daysUntil} ${daysUntil === 1 ? 'day' : 'days'}`,
        color: 'success.main'
      };
    }
    // If trip is ongoing
    else if (now >= startDate && now <= endDate) {
      return {
        text: 'Ongoing trip',
        color: 'primary.main'
      };
    }
    // If trip is in the past
    else {
      const daysAgo = Math.ceil((now - endDate) / (1000 * 60 * 60 * 24));
      return {
        text: `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`,
        color: 'text.secondary'
      };
    }
  };
  
  const daysDiff = calculateDaysDiff();
  
  return (
    <>
      <Card 
        component={motion.div}
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          position: 'relative',
          cursor: 'pointer',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 3
          }
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ position: 'relative', maxHeight: { xs: '140px', sm: '140px', md: '200px' } }}>
          <CardMedia
            component="img"
            sx={{ 
              height: { xs: '140px', sm: '140px', md: '200px' },
              width: '100%',
              position: 'relative',
              objectFit: 'cover',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)',
                zIndex: 1
              }
            }}
            image={imageUrl}
            alt={trip.destination || "Travel destination"}
            onError={(e) => {
              console.error("Failed to load image in TripCard:", e);
              e.target.src = DEFAULT_DESTINATION_IMAGE;
            }}
          />
          
          <Box sx={{ position: 'absolute', bottom: 6, right: 6, display: 'flex', gap: 0.5, zIndex: 2 }}>
            <IconButton
              onClick={handleToggleFavorite}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                color: 'white',
                padding: '3px',
              }}
            >
              <FavoriteIcon 
                color={trip.isFavorite ? 'error' : 'inherit'} 
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              />
            </IconButton>
          </Box>
          
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 8, sm: 8 },
              left: { xs: 8, sm: 8 },
              zIndex: 2
            }}
          >
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                lineHeight: 1.2
              }}
            >
              {trip.destination}
            </Typography>
          </Box>
        </Box>
        
        <CardContent sx={{ 
          flexGrow: 1, 
          pt: { xs: 0.5, sm: 1 }, 
          pb: { xs: 0.5, sm: 1 }, 
          px: { xs: 1, sm: 1.5 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.25, sm: 0.5 } }}>
            <DateRangeIcon sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, color: 'text.secondary', mr: 0.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {formatDateRange(trip.startDate, trip.endDate)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                {trip.travelers} {trip.travelers === 1 ? 'Traveler' : 'Travelers'}
              </Typography>
            </Box>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: daysDiff.color,
                fontWeight: 600,
                fontSize: { xs: '0.65rem', sm: '0.75rem' }
              }}
            >
              {daysDiff.text}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

const SavedTripsPage = () => {
  const [savedTrips, setSavedTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get responsive sizes
  const { fontSize, spacing, borderRadius, buttonSize } = useResponsiveSize();
  
  // Delete functionality is now only for context menu which was removed
  // No need for delete dialog
  
  useEffect(() => {
    // Wait for authentication to complete
    if (!authLoading) {
      if (user) {
        fetchSavedTrips();
      } else {
        // Redirect to login if no user
        navigate('/login', { 
          state: { 
            from: '/saved-trips',
            message: 'Please login to view your saved trips' 
          } 
        });
      }
    }
  }, [user, authLoading, navigate]);
  
  const fetchSavedTrips = async () => {
    try {
      setLoading(true);
      setError(null);
      const trips = await getUserItineraries();
      setSavedTrips(trips);
    } catch (err) {
      console.error('Error fetching saved trips:', err);
      setError('Failed to load your saved trips. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleFavorite = async (id) => {
    try {
      await toggleFavorite(id);
      // Update UI by toggling the favorite status
      setSavedTrips(prevTrips =>
        prevTrips.map(trip =>
          trip.id === id ? { ...trip, isFavorite: !trip.isFavorite } : trip
        )
      );
    } catch (err) {
      console.error('Error toggling favorite status:', err);
      setError('Failed to update favorite status. Please try again.');
    }
  };
  
  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Helper function to create a local date from a string or Date object
  const createLocalDate = (dateStr) => {
    // If it's already a date, return it
    if (dateStr instanceof Date) return dateStr;
    
    // Handle ISO string or YYYY-MM-DD format
    if (typeof dateStr === 'string') {
      // If in YYYY-MM-DD format, parse it locally
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
        return new Date(year, month - 1, day); // month is 0-indexed in JS Date
      }
      // Otherwise use standard Date constructor
      return new Date(dateStr);
    }
    
    // Fallback
    return new Date();
  };
  
  // Helper functions to separate trips by date
  const isUpcomingTrip = (trip) => {
    const now = new Date();
    const endDate = createLocalDate(trip.endDate);
    return endDate >= now;
  };
  
  const isPastTrip = (trip) => {
    const now = new Date();
    const endDate = createLocalDate(trip.endDate);
    return endDate < now;
  };
  
  // Get filtered trips based on active tab and search query
  const getFilteredTrips = () => {
    let filtered = savedTrips;
    
    // Filter by tab
    switch (activeTab) {
      case 0: // All trips
        filtered = savedTrips;
        break;
      case 1: // Upcoming trips
        filtered = savedTrips.filter(isUpcomingTrip);
        break;
      case 2: // Past trips
        filtered = savedTrips.filter(isPastTrip);
        break;
      case 3: // Favorites
        filtered = savedTrips.filter(trip => trip.isFavorite);
        break;
      default:
        filtered = savedTrips;
    }
    
    // Apply search filter if query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(trip => 
        trip.destination.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };
  
  // Sort trips by date
  const sortByDate = (trips, ascending = true) => {
    return [...trips].sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return ascending ? dateA - dateB : dateB - dateA;
    });
  };
  
  // Count trips by category
  const upcomingTripsCount = savedTrips.filter(isUpcomingTrip).length;
  const pastTripsCount = savedTrips.filter(isPastTrip).length;
  const favoriteTripsCount = savedTrips.filter(trip => trip.isFavorite).length;
  
  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 8 
        }}>
          <CircularProgress size={isMobile ? 40 : 48} />
          <Typography variant="body1" sx={{ mt: 2, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            Loading your saved trips...
          </Typography>
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert severity="error" sx={{ 
          mb: 4,
          '& .MuiAlert-message': {
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }
        }}>
          {error}
        </Alert>
      );
    }
    
    const filteredTrips = getFilteredTrips();
    
    if (savedTrips.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 6,
          textAlign: 'center'
        }}>
          <img 
            src={EmptyStateIllustration} 
            alt="No saved trips" 
            style={{ maxWidth: '300px', marginBottom: '1.5rem' }} 
          />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            No saved trips yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 450, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            Your saved trips will appear here. Start planning your next adventure now!
          </Typography>
        </Box>
      );
    }
    
    if (filteredTrips.length === 0) {
      let emptyMessage, emptyTitle;
      switch (activeTab) {
        case 1:
          emptyTitle = "No upcoming trips";
          emptyMessage = "You don't have any upcoming trips planned.";
          break;
        case 2:
          emptyTitle = "No past trips";
          emptyMessage = "You don't have any past trips in your collection.";
          break;
        case 3:
          emptyTitle = "No favorite trips";
          emptyMessage = "Add trips to your favorites by clicking the heart icon on a trip card.";
          break;
        default:
          emptyTitle = "No trips found";
          emptyMessage = "No trips match the current filter.";
      }
      
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 6,
          textAlign: 'center'
        }}>
          <img 
            src={EmptyStateIllustration} 
            alt={emptyTitle} 
            style={{ maxWidth: '300px', marginBottom: '1.5rem' }} 
          />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {emptyTitle}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 450, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            {emptyMessage}
          </Typography>
          <Button 
            onClick={() => setActiveTab(0)}
            variant="contained" 
            color="primary" 
            size="large"
            sx={{ 
              px: 4, 
              py: 1.2, 
              borderRadius: 2, 
              fontWeight: 600,
              boxShadow: theme.shadows[4],
              fontSize: { xs: '0.85rem', sm: '0.95rem' }
            }}
          >
            View All Trips
          </Button>
        </Box>
      );
    }
    
    // For the "All Trips" tab, separate and sort upcoming and past trips
    if (activeTab === 0) {
      const upcomingTrips = sortByDate(filteredTrips.filter(isUpcomingTrip));
      const pastTrips = sortByDate(filteredTrips.filter(isPastTrip), false); // Sort past trips from most recent to oldest
      
      return (
        <>
          {/* Upcoming Trips Section */}
          {upcomingTrips.length > 0 && (
            <>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="600" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Upcoming Trips
                </Typography>
                <Box 
                  sx={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: '16px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 'medium'
                  }}
                >
                  {upcomingTrips.length}
                </Box>
              </Box>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {upcomingTrips.map((trip) => (
                  <Grid item key={trip.id} xs={12} sm={6} md={4} lg={3}>
                    <TripCard 
                      trip={trip} 
                      onToggleFavorite={handleToggleFavorite} 
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          
          {/* Past Trips Section */}
          {pastTrips.length > 0 && (
            <>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="600" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Past Trips
                </Typography>
                <Box 
                  sx={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'text.secondary',
                    color: 'white',
                    borderRadius: '16px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 'medium'
                  }}
                >
                  {pastTrips.length}
                </Box>
              </Box>
              <Grid container spacing={2}>
                {pastTrips.map((trip) => (
                  <Grid item key={trip.id} xs={12} sm={6} md={4} lg={3}>
                    <TripCard 
                      trip={trip} 
                      onToggleFavorite={handleToggleFavorite} 
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      );
    }
    
    // For other tabs (upcoming, past, favorites), just sort and display
    const sortedTrips = activeTab === 2 
      ? sortByDate(filteredTrips, false) // For past trips, sort from recent to oldest
      : sortByDate(filteredTrips); // For other tabs, sort from earliest to latest
    
    return (
      <Grid container spacing={2}>
        {sortedTrips.map((trip) => (
          <Grid item key={trip.id} xs={12} sm={6} md={4} lg={3}>
            <TripCard 
              trip={trip} 
              onToggleFavorite={handleToggleFavorite} 
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 2, md: 3 }, mt: { xs: 6, md: 8 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: { xs: 1.5, sm: 0 },
            mb: { xs: 2, md: 3 }
          }}>
            <Box>
              <Typography 
                variant="h4" 
                component="h2" 
                fontWeight={700}
                sx={{ 
                  mb: 1.5,
                  background: 'linear-gradient(135deg, #15192E 0%, #5E17EB 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '60px',
                    height: '4px',
                    background: 'linear-gradient(90deg, rgba(94, 23, 235, 0.9) 0%, rgba(94, 23, 235, 0.3) 100%)',
                  }
                }}
              >
                My Trips
              </Typography>
              
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                  mb: { xs: 1, sm: 0 }
                }}
              >
                View, manage, and organize your travel itineraries
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              width: { xs: '100%', sm: 'auto' }
            }}>
              <TextField
                placeholder="Search trips..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    height: '40px',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                  }
                }}
                sx={{ 
                  width: { xs: '100%', sm: 200 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: 'background.paper',
                  }
                }}
              />

            </Box>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleChangeTab}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: '40px',
                ml: { xs: -1, sm: 0 },
                '& .MuiTabs-scroller': {
                  pl: { xs: 0, sm: 0 }
                },
                '& .MuiTabs-flexContainer': {
                  justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                },
                '& .MuiTab-root': {
                  minHeight: '40px',
                  fontSize: { xs: '0.7rem', sm: '0.9rem' },
                  px: { xs: 1, sm: 2 },
                  py: 0.5,
                  minWidth: 'auto',
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  },
                  '& .MuiBox-root': {
                    gap: { xs: 0.5, sm: 1 }
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: 1,
                  backgroundColor: 'primary.main'
                }
              }}
            >
              <Tab 
                icon={<FormatListBulletedIcon />}
                iconPosition="start"
                label={<span style={{ whiteSpace: 'nowrap' }}>All Trips</span>}
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, position: 'relative' }}>
                    <DateRangeIcon />
                    <span style={{ whiteSpace: 'nowrap' }}>Upcoming</span>
                    {upcomingTripsCount > 0 && (
                      <Box 
                        sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'primary.main',
                          color: 'white',
                          borderRadius: '50%',
                          width: { xs: 16, sm: 20 },
                          height: { xs: 16, sm: 20 },
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          fontWeight: 'bold'
                        }}
                      >
                        {upcomingTripsCount > 99 ? '99+' : upcomingTripsCount}
                      </Box>
                    )}
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, position: 'relative' }}>
                    <SettingsBackupRestoreIcon />
                    <span style={{ whiteSpace: 'nowrap' }}>Past</span>
                    {pastTripsCount > 0 && (
                      <Box 
                        sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'text.secondary',
                          color: 'white',
                          borderRadius: '50%',
                          width: { xs: 16, sm: 20 },
                          height: { xs: 16, sm: 20 },
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          fontWeight: 'bold'
                        }}
                      >
                        {pastTripsCount > 99 ? '99+' : pastTripsCount}
                      </Box>
                    )}
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, position: 'relative' }}>
                    <FavoriteIcon />
                    <span style={{ whiteSpace: 'nowrap' }}>Favorites</span>
                    {favoriteTripsCount > 0 && (
                      <Box 
                        sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'error.main',
                          color: 'white',
                          borderRadius: '50%',
                          width: { xs: 16, sm: 20 },
                          height: { xs: 16, sm: 20 },
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          fontWeight: 'bold'
                        }}
                      >
                        {favoriteTripsCount > 99 ? '99+' : favoriteTripsCount}
                      </Box>
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>
          
          {renderContent()}
        </motion.div>
      </Box>
    </Container>
  );
};

export default SavedTripsPage; 